import { useRef, useState } from 'react'

import { FileUploadIcon, Upload04Icon } from '@hugeicons/react'
import _ from 'lodash'
import { useController, useFormContext } from 'react-hook-form'
import toastr from 'toastr'

import { useDataProviderContext } from '@/shared/contexts/DataProviderContext'
import buildActiveStorageFilePayload from '@/shared/lib/file/build-active-storage-file-payload'
import { AppIcon } from '@/shared/ui/icons'
import { Button } from '@/shared/ui/Button'

type MissionFileEvidenceControlsProps = {
  isSubmitting: boolean
  isReadonly: boolean
  isCapacityFull: boolean
  submitLabel: string
}

export const MissionFileEvidenceControls = ({
  isSubmitting,
  isReadonly,
  isCapacityFull,
  submitLabel
}: MissionFileEvidenceControlsProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [fileName, setFileName] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const { control, setValue } = useFormContext()
  const { httpClient } = useDataProviderContext()
  const {
    field: { value: mediaId, onChange }
  } = useController({
    name: 'media_id',
    control,
    rules: { required: true }
  })

  const hasMedia = Boolean(mediaId)
  const isBusy = isUploading || isSubmitting
  const isDisabled = isBusy || isCapacityFull || isReadonly

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setIsUploading(true)

    try {
      const filePayload = await buildActiveStorageFilePayload(file)
      const response = await httpClient.post('/user/medias', {
        record: { file: filePayload }
      })
      const record = _.get(response, 'data.record')

      setValue('media', record)
      onChange(record?.id ?? null)
      setFileName(file.name)
    } catch {
      toastr.error(
        'Não foi possível enviar o arquivo. Tente novamente.',
        'Erro no upload'
      )
      onChange(null)
      setFileName(null)
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handlePrimaryAction = () => {
    if (!hasMedia) fileInputRef.current?.click()
  }

  return (
    <div className="flex flex-col gap-3">
      <input
        ref={fileInputRef}
        type="file"
        className="sr-only"
        disabled={isDisabled}
        onChange={handleFileSelect}
      />

      <p className="m-0 flex items-center gap-2 text-sm font-semibold text-gray-900">
        <FileUploadIcon size={18} variant="duotone" className="text-primary" aria-hidden />
        {hasMedia ? 'Arquivo selecionado' : 'Selecione o arquivo da evidência'}
      </p>

      {fileName && (
        <p className="m-0 break-all text-sm text-gray-800">{fileName}</p>
      )}

      {!hasMedia && (
        <p className="m-0 text-xs text-gray-600">
          PDF, documentos ou outros formatos aceitos
        </p>
      )}

      <Button
        type={hasMedia ? 'submit' : 'button'}
        variant="primary"
        size="md"
        showTrailingIcon={false}
        className="w-full justify-center"
        disabled={isDisabled || (!hasMedia && isUploading)}
        onClick={hasMedia ? undefined : handlePrimaryAction}
        leadingIcon={
          isBusy ? (
            <AppIcon name="spinner-third" />
          ) : hasMedia ? (
            <Upload04Icon size={16} variant="stroke" />
          ) : (
            <FileUploadIcon size={16} variant="stroke" />
          )
        }
      >
        {isUploading
          ? 'Enviando arquivo...'
          : isSubmitting
            ? 'Enviando...'
            : hasMedia
              ? submitLabel
              : 'Selecionar evidência'}
      </Button>
    </div>
  )
}
